<template lang="pug">
.page.brand(ref="page")
  loading(
    v-if="$fetchState.pending"
    text="Loading brand..."
    min-height="300px")

  error-layout(v-else-if="$fetchState.error" :error="$fetchState.error")

  template(v-else-if="record")
    .backdrop
      .backdrop__image(v-image="record.main_image_url")

    .container
      section.brand
        .brand__about
          .brand__logo(
            v-image="record.logo_url?.medium_url"
            :alt="record.name")
          .brand__metas
            h1.brand__name(v-text="record.name")
            .brand__address {{ record | brandAddress }}
            social-tag.mt-1(:social-networks="record.social_networks_attributes")

        .brand__cover(v-image="record.main_image_url")
          sharer.brand__sharing

      section.explore
        el-tabs.explore__tabs(v-model="tab")
          el-tab-pane(label="About" name="about")
            .explore__about
              el-row(:gutter="32")
                el-col(
                  :md="record.sub_image_urls?.length || videoLinks.length ? 12 : 24")
                  .description(
                    v-html="record.description")
                el-col(
                  v-if="record.sub_image_urls?.length || videoLinks?.length"
                  :md="12")
                  .photos
                    div.video(
                      v-for="i, k in videoLinks"
                      :key="`video-${k}`"
                      @click="handlePlayVideo(i, k)"
                      v-image="i.thumbnail")
                      strong
                        i.bx.bx-play
                    div(
                      v-for="i, k in record.sub_image_urls"
                      :key="k"
                      @click="preview(i, k)"
                      v-image="i.product")

          el-tab-pane(label="Products" name="products")
            .explore__products
              products-grid(
                v-if="products.length"
                :records="products"
                :productCardType="PRODUCT_CARD_TYPES.BRAND_DETAILS_GRID"
                :track-props="{ zone: 'BRAND_DETAIL_PRODUCTS', brand_id: $route.params.id }")
              empty(v-else large title="Oops! No products from this brand available in your area" description="Here are some similar items to purchase for your store" )

            client-only(
              v-if="showRecommendedProducts && recommendedProducts?.length")
              .explore__recommended
                h2 Recommended for you
                products-grid(
                  :records="recommendedProducts"
                  :productCardType="PRODUCT_CARD_TYPES.BRAND_DETAILS_GRID"
                  :track-props="{ zone: 'BRAND_RECOMMENDED_PRODUCTS', brand_id: $route.params.id }")
    video-player(
      v-if="video"
      :video="video"
      @close="handleClosePlayer"
    )
</template>

<script>
import { mapGetters } from 'vuex'
import ErrorLayout from '~/layouts/error'
import Sharer from '~/components/Sharer'
import ImagesSwiper from '~/components/modals/ImagesSwiper'
import VideoPlayer from '~/components/modals/VideoPlayer'
import ProductsGrid from '~/components/products/ProductsGrid'
import SocialTag from '~/components/tags/SocialTag'
import { ERROR_HANDLING_MANUAL, PRODUCT_CARD_TYPES } from '~/settings'
import { mapVideoLinks } from '~/utilities/normalizers'

export default {
  name: 'BrandDetail',

  components: {
    ProductsGrid,
    ErrorLayout,
    Sharer,
    SocialTag,
    VideoPlayer
  },

  layout: 'catalog',

  data () {
    return {
      record: null,
      video: null,
      products: [],
      recommendedProducts: [],
      videoLinks: [],
      tab: 'products',
      PRODUCT_CARD_TYPES
    }
  },

  async fetch () {
    try {
      const id = this.$route.params.id
      this.record = await this.$api.catalog.getBrandDetails(id, { [ERROR_HANDLING_MANUAL]: true })
      this.$store.commit('history/addBrand', this.record)
      this.videoLinks = mapVideoLinks(this.record.videos_attributes)
      this.products = await this.$api.catalog.searchProducts({ brand_id: id })
        .then(({ results }) => results)

      if (this.showRecommendedProducts && !this.products?.length) {
        this.recommendedProducts = await this.$api.catalog.searchProducts({
          term: this.record.name,
          sort: 'relevance',
          direction: 'desc',
          per_page: 12
        }).then(({ results }) => results)
      }
    } catch (e) {
      const error = { statusCode: e.response?.status || 404 }
      if (process.server) {
        this.$nuxt.context.res.statusCode = error.statusCode
        this.$fetchState.error = error
      } else {
        this.$nuxt.error(error)
      }
    }
  },

  head () {
    return this.$seo.head(this.seo)
  },

  computed: {
    ...mapGetters({
      isBuyer: 'authentication/isBuyer',
      isHeadBuyer: 'authentication/isHeadBuyer',
      isPreApprovalBuyer: 'authentication/isPreApprovalBuyer'
    }),

    showRecommendedProducts () {
      return this.isBuyer || this.isHeadBuyer
    },

    seo () {
      if (!this.record) return {}
      return {
        title: this.record.name,
        description: this.record.description,
        image: this.record ? this.record.main_image_url : null,
        type: 'og:brand',
        properties: {
          'product:price:currency': 'USD'
        },
        brand: {
          id: this.record.id,
          name: this.record.name
        }
      }
    }
  },

  mounted () {
    const widget = this.$route.query.widget
    if (widget) {
      const brand = this.$route.query.brand
      // set preferences
      this.$preferences.set('vendorBrandWidget', widget)
      this.$preferences.set('vendorBrand', brand)
      // track click into amplitude
      this.$amplitude.track('VISIT_FROM_WHOLESALE_WIDGET', {
        zone: widget,
        brand_name: brand,
        brand_id: +this.$route.params.id
      })
    }
  },

  jsonld () {
    return this.$seo.jsonld(this.seo)
  },

  methods: {
    handleClosePlayer () {
      this.video = null
    },
    handlePlayVideo (video) {
      this.video = video
    },
    preview (image, index) {
      this.$dialogs.open({
        parent: this,
        component: ImagesSwiper,
        props: {
          trackEvent: 'ZOOM_BRAND_PHOTO',
          trackProps: { zone: 'BRAND_DETAIL' },
          initialIndex: index,
          images: this.record?.sub_image_urls
            ?.map(e => ({ ...e, url: e.large })) || []
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.page.brand {
  background: $--color-placeholder;
  position: relative;
  z-index: 1;
  padding: 1.5rem 0 4rem;
  @include media(sm) {
    padding: 2.5rem 0 4rem;
  }
}
.backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 360px;
  z-index: -1;
  overflow: hidden;
  &__image {
    position: absolute;
    top: -48px;
    left: -48px;
    right: -48px;
    height: 300px;
    background-size: cover;
    background-position: center bottom;
    filter: blur(48px) opacity(.5);
  }
}
section.brand {
  display: flex;
  align-items: stretch;
  border-radius: 16px 16px 0 0;
  background: white;
  padding: 8px;
  flex-direction: column-reverse;
  @include media(sm) {
    flex-direction: row;
    gap: 8px;
  }
  > * {
    min-width: 0;
  }
}
.brand {
  &__about {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
    text-align: center;
    justify-content: center;
    padding: 0 12px;
    @include media(xs-only) {
      margin-top: -48px;
      padding-bottom: 1rem;
    }
  }
  &__logo {
    aspect-ratio: 1/1;
    width: 120px;
    background-color: white;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    border-radius: 6px;
    z-index: 1;
    @include media(sm-down) {
      border: 3px solid white;
      width: 96px;
    }
  }
  &__name {
    margin: 4px 0 0 0;
    font-size: 1.25em;
    @include media(sm) {
      margin: 8px 0 0 0;
    }
    @include media(md) {
      font-size: 1.5em;
    }
  }
  &__address {
    color: $--color-text-secondary;
    margin: 8px 0 0 0;
    font-size: 13px;
  }
  &__cover {
    flex: 2;
    aspect-ratio: 8/3;
    background-size: cover;
    background-position: center;
    border-radius: 8px;
    position: relative;
  }
  &__sharing {
    position: absolute;
    top: 12px;
    right: 12px;
    ::v-deep {
      a {
        background: rgba(white, .9);
        backdrop-filter: blur(16px);
        padding: 4px 12px;
        border-radius: 16px;
        color: $--color-text-regular;
        font-size: 13px;
        &:hover {
          background: white;
        }
        i {
          display: none !important;
        }
        &:before {
          content: 'Share';
        }
      }
    }
  }
}
.explore {
  &__tabs {
    ::v-deep {
      .el-tabs {
        &__nav-wrap {
          &:after {
            display: none;
          }
        }
        &__nav {
          float: none;
          margin: 0 auto;
          width: fit-content;
        }
        &__header {
          margin-bottom: 24px;
          user-select: none;
          position: sticky;
          background: rgba(white, .5);
          backdrop-filter: blur(16px);
          border-radius: 0 0 12px 12px;
          top: 0;
          z-index: 1;
        }
        &__item {
          font-weight: $--font-weight-bold;
          line-height: 44px;
          height: 44px;
        }
      }
    }
  }
  &__about {
    background: white;
    border-radius: 16px;
    padding: 24px 16px;
    @include media(md) {
      padding: 24px;
    }
    .description {
      color: $--color-text-secondary;
      font-size: 15px;
      line-height: 150%;
      margin: 0;
    }
    .photos {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(128px, 1fr));
      gap: 6px;
      @include media(sm-down) {
        margin-top: 28px;
      }
      > * {
        min-width: 0;
        aspect-ratio: 1;
        background-size: cover;
        background-position: center;
        background-color: $--color-placeholder;
        border-radius: $--radius;
        cursor: pointer;
      }

      .video {
        position: relative;

        &:hover {
          strong {
            transform: translate(50%, -50%) scale(1.2);
            background: rgba(black, .3);
          }
        }

        strong {
          position: absolute;
          top: 50%;
          right: 50%;
          transform: translate(50%, -50%);
          background: rgba(black, .2);
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 100%;
          font-size: 2rem;
          color: white;
          padding-left: .15em;
          transition: all .2s ease-in-out;
          backdrop-filter: blur(8px);
          pointer-events: none;
        }
      }

    }
  }

  &__recommended {
    h2 {
      text-align: center;
      font-size: 16px;
      margin: 2em 0;
    }
  }
}
</style>
